import {
  Getters,
  Mutations,
  Actions,
  Module,
  createMapper,
} from 'vuex-smart-module';
import ApiFactory from '@/api/apiFactory';
import { getCookie } from '@/utils/cookies';

const PaymentsRepository = ApiFactory.get('payments');
const CountryRepository = ApiFactory.get('country');
const CurrencyRepository = ApiFactory.get('currency');

interface Country {
  id: number;
  label: string;
  shortCode: string;
  code: string;
  defaultCurrencyId: number;
}

interface Currency {
  id: number;
  label: string;
  icon: string;
  code: string;
  popular: boolean;
}

class PaymentsState {
  selectedCountry = {} as Country;

  selectedCurrency = {} as Currency;

  countryList = [] as Country[];

  currencyList = [] as Currency[];

  paymentsList = [];

  locale = '';

  onLoad = true;

  initiated = false;
}

class PaymentsGetters extends Getters<PaymentsState> {
  get selectedCountry() {
    return this.state.selectedCountry ? this.state.selectedCountry : this.state.countryList[0];
  }

  get сountryList() {
    return this.state.countryList ? this.state.countryList : [];
  }

  get initiated() {
    return this.state.initiated;
  }

  get сountryPopularList() {
    return this.state.countryList ? this.state.countryList
      .filter((item: any) => !!item.popular) : [];
  }

  get selectedCurrency() {
    return this.state.selectedCurrency ? this.state.selectedCurrency : this.state.currencyList[0];
  }

  get currencyList() {
    return this.state.currencyList ? this.state.currencyList : [];
  }

  get currencyPopularList() {
    return this.state.currencyList ? this.state.currencyList
      .filter((item: any) => !!item.popular) : [];
  }

  get paymentsList() {
    return this.state.paymentsList;
  }

  get restoreCurrencyList() {
    return ((codeList: any) => this.state.currencyList.reduce((previosValue: any, curentValue: any) => {
      if (codeList.indexOf(curentValue.code) !== -1) {
        previosValue.push(curentValue);
      }
      return previosValue;
    }, [])
    );
  }

  get onLoad() {
    return this.state.onLoad;
  }
}

class PaymentsMutations extends Mutations<PaymentsState> {
  selectCountry(itemId: number): void {
    this.state.selectedCountry = this.state.countryList.find((item: any) => item.id === itemId) || {} as Country;
  }

  selectCurrency(itemId: number): void {
    this.state.selectedCurrency = this.state.currencyList.find((item: any) => item.id === itemId) || {} as Currency;
  }

  fillCountry(payload: any) {
    this.state.countryList = payload;
    this.state.selectedCountry = {} as Country;
  }

  fillCurrency(payload: any) {
    this.state.currencyList = payload;
    this.state.selectedCurrency = {} as Currency;
  }

  fillPayments(payload: any) {
    this.state.paymentsList = payload;
  }

  selectLocale(payload: any) {
    this.state.locale = payload;
  }

  load(payload: boolean) {
    this.state.onLoad = payload;
  }

  toggleItemPopover(payload: any) {
    const itemInList: any = this.state.paymentsList.find((item: any) => item.name === payload.itemName);

    if (itemInList) {
      itemInList.popupIsShown = payload.state;
    }
  }

  setInitiated(payload: boolean) {
    this.state.initiated = payload;
  }
}

class PaymentsActions extends Actions<
  PaymentsState,
  PaymentsGetters,
  PaymentsMutations,
  PaymentsActions
> {
  async loadCountries(locale: string) {
    const countryList = await CountryRepository.get(locale);
    this.commit('fillCountry', countryList);
    return countryList;
  }

  async loadCurrencies(locale: string) {
    const currencyList = await CurrencyRepository.get(locale) || [];

    currencyList.forEach((item: any) => {
      // eslint-disable-next-line no-param-reassign
      item.shortCode = item.code;
      // eslint-disable-next-line no-param-reassign
      item.label = `${item.shortCode} - ${item.label}`;
    });

    this.commit('fillCurrency', currencyList);
    return currencyList;
  }

  async updatePayments() {
    const {
      selectedCurrency,
      locale,
      selectedCountry,
    } = this.state;
    this.commit('load', true);
    const selectedCountryId = (selectedCountry as any).id;
    const selectedCurrencyId = (selectedCurrency as any).id;
    const key = localStorage.getItem('PUNTER_KEY');
    let headers;
    if (key && key.length) {
      headers = {
        headers: {
          'Punter-Key': key,
        },
      };
    } else {
      headers = {
      };
    }

    const paymentsList = await PaymentsRepository.get(locale, selectedCountryId, selectedCurrencyId, headers);
    const {
      providerList,
      country,
      currency,
    } = paymentsList;

    providerList.forEach((item: any) => {
      // eslint-disable-next-line no-param-reassign
      item.popupIsShown = false;
    });
    if (country && country.id) {
      this.commit('selectCountry', country.id);
    }
    if (currency && currency.id) {
      this.commit('selectCurrency', currency.id);
    }

    this.commit('fillPayments', providerList);
    if (!this.state.initiated) {
      this.commit('setInitiated', true);
    }
    this.commit('load', false);
  }

  async loadPayments() {
    const {
      locale,
    } = this.state;
    this.commit('load', true);
    const key = localStorage.getItem('PUNTER_KEY');
    let headers;
    if (key && key.length) {
      headers = {
        headers: {
          'Punter-Key': key,
        },
      };
    } else {
      headers = {
      };
    }

    const paymentsList = await PaymentsRepository.get(locale, null, null, headers);
    const {
      providerList,
      country,
      currency,
    } = paymentsList;

    providerList.forEach((item: any) => {
      // eslint-disable-next-line no-param-reassign
      item.popupIsShown = false;
    });
    if (country && country.id) {
      this.commit('selectCountry', country.id);
    }
    if (currency && currency.id) {
      this.commit('selectCurrency', currency.id);
    }

    this.commit('fillPayments', providerList);
    if (!this.state.initiated) {
      this.commit('setInitiated', true);
    }
    this.commit('load', false);
  }

  async init(locale: string) {
    this.commit('selectLocale', locale);
    const countryList = await this.dispatch('loadCountries', locale) || [];
    await this.dispatch('loadCurrencies', locale);
    if (countryList[0]) {
      this.commit('selectCountry', countryList[0].id);
      this.commit('selectCurrency', countryList[0].defaultCurrencyId);
    }
    await this.dispatch('loadPayments');
  }

  async updateCountry(payload: any) {
    this.commit('selectCountry', payload.id);
    this.commit('selectCurrency', payload.defaultCurrencyId);
    await this.dispatch('updatePayments');
  }

  async updateCurrency(currencyCode: number) {
    this.commit('selectCurrency', currencyCode);
    await this.dispatch('updatePayments');
  }
}

// Экспорт модуля
export const PaymentModule = new Module({
  state: PaymentsState,
  getters: PaymentsGetters,
  mutations: PaymentsMutations,
  actions: PaymentsActions,
});

export const PaymentModuleMapper = createMapper(PaymentModule);
